import { jsonForScript } from "./htmlViewer/bootstrapScript";

export const chiliPiperIframeSandbox =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads";

export type ChiliPiperLead = {
  FirstName: string;
  Email: string;
  LastName?: string;
  Company?: string;
};

// Parent -> shell.
export type ChiliPiperOpenMessage = {
  type: "3b-chili-piper-open";
  lead: ChiliPiperLead;
};

// Shell -> parent, translated from ChiliPiper's own lifecycle events.
export type ChiliPiperShellMessage =
  | { type: "3b-chili-piper-ready" }
  | { type: "3b-chili-piper-availability"; hasAvailability: boolean }
  | { type: "3b-chili-piper-booked" }
  | { type: "3b-chili-piper-closed" }
  | { type: "3b-chili-piper-error" };

export function chiliPiperShellHtml(opts: {
  parentOrigin: string;
  chiliPiperOrigin: string;
  scriptUrl: string;
  tenant: string;
  router: string;
  campaignId?: string;
  nonce: string;
}): string {
  const script = `(function () {
    var PARENT_ORIGIN = ${jsonForScript(opts.parentOrigin)};
    var CHILI_PIPER_ORIGIN = ${jsonForScript(opts.chiliPiperOrigin)};
    var SCRIPT_URL = ${jsonForScript(opts.scriptUrl)};
    var TENANT = ${jsonForScript(opts.tenant)};
    var ROUTER = ${jsonForScript(opts.router)};
    var CAMPAIGN_ID = ${jsonForScript(opts.campaignId ?? "")};
    var pendingLead = null;
    var submitted = false;

    function post(message) {
      parent.postMessage(message, PARENT_ORIGIN);
    }

    // Only forward the string fields we expect, so a hostile parent can't seed
    // ChiliPiper's lead with arbitrary objects.
    function sanitizeLead(lead) {
      if (!lead || typeof lead !== "object") return null;
      var out = {
        FirstName: String(lead.FirstName || ""),
        Email: String(lead.Email || ""),
      };
      if (typeof lead.LastName === "string" && lead.LastName) {
        out.LastName = lead.LastName;
      }
      if (typeof lead.Company === "string" && lead.Company) {
        out.Company = lead.Company;
      }
      return out;
    }

    function maybeSubmit() {
      if (submitted || !pendingLead || !window.ChiliPiper) return;
      submitted = true;
      var options = {
        map: false,
        lead: pendingLead,
        title: "Thanks! What time works best for a quick call?",
        domElement: "#host",
        onError: function () { post({ type: "3b-chili-piper-error" }); },
      };
      if (CAMPAIGN_ID) options.campaignId = CAMPAIGN_ID;
      window.ChiliPiper.submit(TENANT, ROUTER, options);
    }

    window.addEventListener("message", function (event) {
      if (event.origin === PARENT_ORIGIN && event.source === window.parent) {
        var data = event.data;
        if (data && data.type === "3b-chili-piper-open") {
          pendingLead = sanitizeLead(data.lead);
          maybeSubmit();
        }
        return;
      }
      // ChiliPiper's own booking iframe reports lifecycle via a bare
      // { action } payload from its origin; translate it into our typed set.
      if (event.origin === CHILI_PIPER_ORIGIN) {
        var action = event.data && event.data.action;
        if (action === "availability-loaded") {
          post({ type: "3b-chili-piper-availability", hasAvailability: true });
        } else if (action === "no-free-slots") {
          post({ type: "3b-chili-piper-availability", hasAvailability: false });
        } else if (action === "booking-confirmed") {
          post({ type: "3b-chili-piper-booked" });
        } else if (action === "close") {
          post({ type: "3b-chili-piper-closed" });
        } else if (action === "error") {
          post({ type: "3b-chili-piper-error" });
        }
      }
    });

    var widget = document.createElement("script");
    widget.async = true;
    widget.crossOrigin = "anonymous";
    widget.src = SCRIPT_URL;
    widget.onload = maybeSubmit;
    widget.onerror = function () { post({ type: "3b-chili-piper-error" }); };
    document.head.appendChild(widget);

    // The parent reads prolonged silence after the frame's load event as a
    // shell that never ran, so this must be posted unconditionally.
    post({ type: "3b-chili-piper-ready" });
  })();`;

  return `<!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8">
              <link rel="icon" href="data:,">
              <style>
                html, body { margin: 0; padding: 0; height: 100%; background: transparent; }
                #host { position: fixed; inset: 0; }
              </style>
            </head>
            <body>
              <div id="host"></div>
              <script nonce="${opts.nonce}">${script}</script>
            </body>
          </html>`;
}
